import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { filter } from 'rxjs/operators';
import { OrganigramApiService, OrganigramProcess, OrganigramSessionSummary } from './organigram-api.service';
import { CustomerdatabaseService } from './customerdatabase.service';
import { OrgTraceHop, hopLabel, hopsForRequest, mergeHops, nodeIdsOf } from './organigram-chat-trace';
import { OrgTask, TASKS_ENABLED, localDateKey, tasksFromDocs, todosWithDone } from './organigram-tasks';
import { isChattableNodeType } from './organigram-chat-directory';
import { OrganigramPermissionsService } from './organigram-permissions.service';
import { organigramAccessDenied } from '../mona-app/workflow-graph/logic/organigram-permission';

export interface OrgChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  // The organigram hops behind this answer, shown as a collapsible "thinking" block (assistant only).
  trace?: OrgTraceHop[];
  traceOpen?: boolean;   // expanded - the block opens only when the reader asks for it
  traceDone?: boolean;
  traceMs?: number;      // how long the request took, for the settled label
  traceLabel?: string;   // live header while running ("Asking Legal..."), paced by pumpLabel
}

// One bounded conversation with a node. Many conversations can exist per node (fresh context
// each time is better for agents), grouped by nodeId in the rail's per-agent history.
export interface OrgConversation {
  id: string;
  organigramId: string;
  nodeId: string;
  nodeName: string;
  nodeType?: string;
  title: string;
  messages: OrgChatMessage[];
  draft: string;
  sending: boolean;
  open: boolean;        // currently shown as a bottom window
  minimized: boolean;
  createdAt: number;
  updatedAt: number;
  requestId?: string;                                                // the backend request this convo started (for matching)
  sessionId?: string;                                                // backend chat session - replayed so follow-ups keep context
  pendingConfirmation?: { executionId: string; message: string };   // an execution paused, awaiting approve/reject
  confirmExecutionId?: string;                                       // the approval this convo hosts - a durable rebind key (survives a transient clear) so a re-reported /pending item, node-less ones included, reuses this card instead of duplicating
  confirming?: boolean;                                              // decision is being submitted (disables the buttons)
  unread?: boolean;                                                  // new activity while not the focused panel (shows a bottom chat head)
  loadingHistory?: boolean;                                          // pulling this session's history from the backend
  traceIndex?: number;                                               // message index the in-flight trace attaches to (undefined = nothing running)
  traceStart?: number;                                               // when the in-flight request started, for the "Thought for Xs" label
  traceNodeIds?: string[];                                           // nodes this conversation's live trace touched (the graph highlights them)
  traceLabelQueue?: string[];                                        // live labels waiting their turn (see LABEL_DWELL_MS)
  traceLabelAt?: number;                                             // when the current live label was shown
  traceShown?: number;                                               // hops already turned into a label, so a poll does not re-queue them
}

// A 'waiting_for_human' execution to surface as an approval card, fed in from the live process feed.
// `history` is the request's lead-up trace, shown as context when no existing conversation matches.
export interface ConfirmationRequest {
  executionId: string; organigramId: string; nodeId: string; nodeName: string; nodeType?: string; message: string;
  requestId?: string; history?: OrgChatMessage[];
}

// Hops can land a few hundred ms apart - faster than a name can be read - so each live label is held
// this long before the next replaces it. Purely cosmetic: the answer never waits for the queue.
const LABEL_DWELL_MS = 900;
// Sessions come back newest first; only this many are turned into conversations, so a long-lived
// account does not flood the rail's history with hundreds of old chats.
const MAX_HYDRATED_SESSIONS = 50;
// An org task has no upper bound - it can sit behind a human, a slow tool call or a deep fan-out -
// so the reply poll is not capped. The interval widens with the wait: a normal reply still lands at
// once, while a job that runs for an hour costs a few requests a minute instead of hundreds.
const REPLY_FAST_MS = 1500;                    // for the first REPLY_FAST_UNTIL_MS
const REPLY_MEDIUM_MS = 5000;                  // up to REPLY_MEDIUM_UNTIL_MS
const REPLY_SLOW_MS = 15000;                   // beyond that, for as long as it takes
const REPLY_FAST_UNTIL_MS = 30 * 1000;
const REPLY_MEDIUM_UNTIL_MS = 5 * 60 * 1000;

function replyPollDelay(waited: number): number {
  if (waited < REPLY_FAST_UNTIL_MS) { return REPLY_FAST_MS; }
  return waited < REPLY_MEDIUM_UNTIL_MS ? REPLY_MEDIUM_MS : REPLY_SLOW_MS;
}

// Backend timestamps arrive as ISO strings, epoch seconds, or Firestore {seconds} - normalize to ms.
function toMillis(value: any): number {
  if (!value) { return Date.now(); }
  if (typeof value === 'number') { return value < 1e12 ? value * 1000 : value; }
  if (typeof value === 'string') { const t = Date.parse(value); return isNaN(t) ? Date.now() : t; }
  if (typeof value.seconds === 'number') { return value.seconds * 1000; }
  return Date.now();
}

// The backend's history shape is ChatMessage {role: 'user' | 'model', text} - 'model' being its word
// for the assistant side. The other shapes below ({role, content}, a bare string, a
// {user_message, response} turn) are tolerated so a stored or mocked history still renders.
function toChatMessages(history: any): OrgChatMessage[] {
  const raw = Array.isArray(history) ? history : (history?.messages || history?.history || []);
  const out: OrgChatMessage[] = [];
  for (const m of raw || []) {
    if (typeof m === 'string') { out.push({ role: 'assistant', content: m }); continue; }
    if (!m) { continue; }
    if (m.user_message) { out.push({ role: 'user', content: String(m.user_message) }); }
    const content = m.content ?? m.text ?? m.message ?? m.response ?? '';
    if (!content) { continue; }
    const role = String(m.role || '').toLowerCase();
    // A turn that already emitted its user half is answered by the assistant, whatever `role` says.
    const mapped: OrgChatMessage['role'] = m.user_message ? 'assistant'
      : role === 'user' || role === 'human' ? 'user'
      : role === 'system' ? 'system' : 'assistant';
    out.push({ role: mapped, content: String(content) });
  }
  return out;
}

function collapsedText(value: string): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function isGenericTitle(title: string): boolean {
  const t = (title || '').trim().toLowerCase();
  return !t || t === 'chat' || t === 'new chat' || t.startsWith('chat with ');
}

function firstUserSnippet(messages: OrgChatMessage[]): string {
  for (const m of messages || []) {
    if (m.role !== 'user') { continue; }
    const t = collapsedText(m.content);
    if (t) { return t.slice(0, 80); }
  }
  return '';
}

// Owns the chat dock UI state and the working set of conversations. The BACKEND is the source of
// truth: the list hydrates from GET /sessions (loadAllSessions) and each chat's messages from
// getSessionHistory on open. Nothing is persisted to localStorage - a stale local copy would mask a
// broken backend and drift from it - so the dock starts empty on refresh and reflects the backend.
// Approvals come from the live listener + the /pending poll. Polling runs in the background.
@Injectable({ providedIn: 'root' })
export class OrganigramChatDockService implements OnDestroy {
  conversations: OrgConversation[] = [];
  railOpen = false;
  // Width in px of a route-level panel docked to the right edge (0 = the edge is free). The bottom
  // bar shifts left by this much so it never sits on that panel's own controls - the same move it
  // already makes for its own chat window.
  edgePanelPx = 0;
  hydratingSessions = true;   // true until the first backend session load lands (Recent shows a loader, not "empty")
  awaitingNodeIds = new Set<string>();   // node ids with a pending confirmation (read by the graph for its pulse)
  activeNodeIds = new Set<string>();     // node ids with a message in flight right now (read by the graph for its highlight)
  focusedId: string | null = null;       // conversation the big panel shows - other views (the graph's composer island) set this to open a chat they started
  private seq = 0;
  // The org this app scopes to - owned by OrganigramApiService, which tracks the switcher selection.
  private get organigramId(): string { return this.api.currentOrganigramId; }
  private confirmUnsub: (() => void) | null = null;
  private confirmWantListen = false;
  private confirmGen = 0;   // guards stale app2 retries across company switches
  // Node labels are cross-company now (the dock spans every company), so the cache is keyed by org:
  // orgId -> (nodeId -> name/type). Each company's graph is fetched once, on demand.
  private orgNodeInfo = new Map<string, Map<string, { name: string; type?: string }>>();
  private orgNodeLoading = new Set<string>();          // orgs whose graph fetch is in flight (de-dupes)
  private companyNames = new Map<string, string>();    // orgId -> company name, for the row/card chips
  private pendingPollTimer: any = null;                // polls the cross-company pending list
  // Pending approvals have two sources that must not overwrite each other: the cross-company REST list
  // (GET /pending, complete, polled) and the current-org live listener (fresh, but current company only).
  private apiPending: ConfirmationRequest[] = [];
  private livePending: ConfirmationRequest[] = [];
  private labelTimers = new Map<string, any>();   // conversation id -> the live-label pump's pending timer
  tasks: OrgTask[] = [];        // dailyTodos assigned to this person - see loadTasks
  tasksLoading = false;
  // The id every account-scoped load gates on, via the organigram API's shared resolver so the blank-
  // customerDB fallback lives in ONE place (OrganigramApiService.resolveUserId). Without a non-blank id,
  // whenUserId/loadAllSessions/pollPending/loadTasks all bail before calling the backend and the dock
  // reads empty - so it must resolve the id the same way the request headers do.
  private get userId(): string { return this.api.resolveUserId(); }

  // The dock shows organigram chats, so it follows the same rule as the /organigram page: without
  // organigram.view it reads nothing and the component hides. Only a real refusal blocks - an unknown
  // answer (not asked yet, 5xx, timeout) leaves the dock exactly as it was before permissions existed.
  get blocked(): boolean { return organigramAccessDenied(this.permissions.current); }
  private wasBlocked = false;

  // True once the permission answer is in and allows the dock. Every backend read waits on it, so no
  // chat is fetched - or flashed on screen - before a refusal can land.
  mayRead(): Promise<boolean> { return this.permissions.settled().then(() => !this.blocked); }

  constructor(private api: OrganigramApiService, private customerDB: CustomerdatabaseService, private zone: NgZone,
              private permissions: OrganigramPermissionsService) {
    // The backend is the source of truth: the chat list, company list and pending approvals all come
    // from account-scoped endpoints (X-Account-Id + X-User-Id), which are only populated once the
    // customer DB has authenticated AND resolved the account - so wait for that signal, not construction.
    // It re-emits on re-login and every load self-dedupes, so re-running is safe. Nothing is restored
    // from localStorage: a stale local copy would mask a backend failure (a broken backend would still
    // look healthy), so the dock deliberately starts empty and reflects whatever the backend returns.
    // The signal emits from connectDB BEFORE the user id is populated (setUserStorage runs just after),
    // and it does NOT re-emit when the id later resolves. These loads all need X-User-Id, so wait for it
    // via whenUserId - firing once here would skip the whole first hydration.
    this.customerDB.isAuthenticatedOnCustDb$.pipe(filter(Boolean)).subscribe(() => this.whenUserId(() => this.hydrate()));
    // The live Firestore listener is per-company (it needs one collection path), so re-point it when
    // the user switches. The cross-company REST pending list (apiPending) is NOT reset here - it spans
    // every company and must survive the switch. Fires immediately for the initial id.
    this.api.currentOrganigramId$.subscribe(() => {
      this.livePending = [];
      if (this.confirmUnsub) { this.confirmUnsub(); this.confirmUnsub = null; }
      this.ensureOrgNodes(this.organigramId);   // load the new company's node names (cached)
      this.startConfirmationListener();         // app-level & always-on, so confirmations surface on ANY page
      this.publishPending();                    // re-render immediately with the still-intact global list
    });
    // A refusal that lands later - or a grant that comes back - switches every feed off or on again.
    this.permissions.load$.subscribe(() => {
      if (this.blocked === this.wasBlocked) { return; }
      this.wasBlocked = this.blocked;
      if (this.blocked) { this.shutDown(); return; }
      this.whenUserId(() => this.hydrate());
      this.startConfirmationListener();
    });
  }

  // The account-scoped loads, once the permission answer allows them.
  private async hydrate(): Promise<void> {
    if (!await this.mayRead()) { this.hydratingSessions = false; return; }
    if (!this.conversations.length) { this.hydratingSessions = true; }   // Recent shows the loader again after a lifted refusal
    this.loadCompanyNames();   // orgId -> name, for the company chips + Browse picker
    this.startPendingPoll();   // cross-company approvals: independent of the switcher, so nothing is missed
    this.loadAllSessions();    // the Recent list, straight from the backend (every company)
    this.loadTasks();          // dailyTodos: where a message sent to a human node actually lands
  }

  // A refusal landed: stop every feed and drop everything already read, so nothing stays readable in
  // memory either (the component hides itself on the same flag).
  private shutDown(): void {
    this.confirmWantListen = false;
    this.confirmGen++;
    if (this.confirmUnsub) { this.confirmUnsub(); this.confirmUnsub = null; }
    if (this.pendingPollTimer) { clearInterval(this.pendingPollTimer); this.pendingPollTimer = null; }
    for (const t of this.labelTimers.values()) { clearTimeout(t); }
    this.labelTimers.clear();
    this.conversations = [];
    this.apiPending = [];
    this.livePending = [];
    this.tasks = [];
    this.awaitingNodeIds = new Set<string>();
    this.activeNodeIds = new Set<string>();
    this.orgNodeInfo.clear();
    this.companyNames.clear();
    this.railOpen = false;
    this.focusedId = null;
    this.hydratingSessions = false;
  }

  // Conversations currently open as bottom windows.
  get windows(): OrgConversation[] { return this.conversations.filter(c => c.open); }

  // Past conversations with a node, newest first (the per-agent history).
  conversationsFor(nodeId: string): OrgConversation[] {
    return this.conversations.filter(c => c.nodeId === nodeId).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  // Start a fresh conversation with a node and open it as a window (the default action).
  //
  // Returns null for a node that is not a chat target - a HUMAN (a message aimed at one lands in
  // dailyTodos as a task rather than starting a conversation) or a TRIGGER (a workflow starter with
  // no reader). Refused here rather than only in the views because five separate surfaces call this -
  // the two org views, the deprecated node detail, and the dock's own directory and "new chat with
  // the same node" - and a rule enforced in each of them is one somebody adds a sixth caller past.
  // The callers hide their own affordances too, so this backstop should never be what the user runs
  // into.
  startNew(organigramId: string, nodeId: string, nodeName: string, nodeType?: string): OrgConversation | null {
    if (!isChattableNodeType(nodeType)) {
      console.warn('[OrgChatDock] refusing to start a chat with a non-chattable node (human or trigger):', nodeId, nodeType);
      return null;
    }
    const now = Date.now();
    const convo: OrgConversation = {
      id: `c${++this.seq}-${now}`,
      organigramId, nodeId, nodeName, nodeType,
      title: 'New chat',
      messages: [], draft: '', sending: false, open: true, minimized: false,
      createdAt: now, updatedAt: now
    };
    this.conversations.push(convo);
    this.railOpen = true;
    this.save();
    return convo;
  }

  // Reconcile the pending approvals from this service's listener: surface each 'waiting_for_human'
  // execution as an approval card in a chat window for its node - reusing an open conversation with that
  // node, or opening one. Cards whose execution is no longer waiting are dropped. `awaitingNodeIds` then
  // tracks every pending node for the
  // graph's pulse.
  setPendingConfirmations(list: ConfirmationRequest[], organigramId: string): void {
    const wanted = new Set(list.map(pc => pc.executionId));
    for (const c of this.conversations) {
      if (c.pendingConfirmation && !wanted.has(c.pendingConfirmation.executionId)) {
        c.pendingConfirmation = undefined;
      }
    }
    const attached = new Set(this.conversations.filter(c => c.pendingConfirmation).map(c => c.pendingConfirmation!.executionId));
    for (const pc of list) {
      if (attached.has(pc.executionId)) { continue; }
      // A human approval belongs to the APPROVER - the human node the escalation task sits on - not to
      // whoever started the request. A secretary can type a request on the president's behalf, but only
      // the president approves it, so the card must open on the human node's own chat and never be pulled
      // back into the chatting agent. Match an open conversation on that node, else its most recent one,
      // else open a fresh one. It deliberately does NOT match on requestId any more: that keyed the card
      // to the requester's chat, surfacing it to the wrong person. Node matches are scoped to the item's
      // OWN company, so a pending in one company never lands on a same-named node in another.
      const org = pc.organigramId || organigramId;
      // Rebind first to the conversation that already hosts THIS execution (confirmExecutionId) - a
      // durable key that survives the transient clear above, so a re-reported approval reuses its card.
      // It is the ONLY rebind path for a cross-company /pending item, which carries no node_id (nodeId
      // '') and so cannot node-match; without it a one-tick gap in the pending list (an empty poll, a
      // resolve-then-relag) would spawn a fresh orphan every poll. Then fall back to node matching: an
      // open conversation on the approver node, else its most recent one.
      let convo = (pc.executionId ? this.conversations.find(c => c.confirmExecutionId === pc.executionId) : undefined)
        || (pc.nodeId ? this.conversations.find(c => c.nodeId === pc.nodeId && c.organigramId === org && c.open) : undefined)
        || (pc.nodeId ? this.conversations.filter(c => c.nodeId === pc.nodeId && c.organigramId === org).sort((a, b) => b.updatedAt - a.updatedAt)[0] : undefined);
      if (!convo) {
        const now = Date.now();
        convo = {
          id: `c${++this.seq}-${now}`, organigramId: org, nodeId: pc.nodeId, nodeName: pc.nodeName, nodeType: pc.nodeType,
          requestId: pc.requestId, title: 'Approval needed', messages: (pc.history || []).slice(), draft: '',
          sending: false, open: true, minimized: false, createdAt: now, updatedAt: now
        };
        this.conversations.push(convo);
      } else {
        convo.open = true; convo.minimized = false;
        // A surfaced conversation with no history of its own gets the request's trace as context.
        if (!convo.messages.length && pc.history?.length) { convo.messages = pc.history.slice(); }
      }
      convo.pendingConfirmation = { executionId: pc.executionId, message: pc.message };
      convo.confirmExecutionId = pc.executionId;   // durable rebind key (see the match above)
      convo.unread = true;   // a pending approval - surface as an unread bottom chat head if not focused
      convo.updatedAt = Date.now();
    }
    this.awaitingNodeIds = new Set(this.conversations.filter(c => c.pendingConfirmation).map(c => c.nodeId));
    this.save();
  }

  // Approve/reject the execution paused at this conversation's node, then clear the card and note the
  // outcome.
  async confirmConversation(convo: OrgConversation, approved: boolean): Promise<void> {
    const pc = convo.pendingConfirmation;
    if (!pc || convo.confirming) { return; }
    convo.confirming = true;
    try {
      await this.api.confirmHumanAction(convo.organigramId, pc.executionId, approved);
      // Drop it from BOTH sources: the next publish would otherwise re-attach the card in the gap
      // before the listener sees the status write.
      this.apiPending = this.apiPending.filter(p => p.executionId !== pc.executionId);
      this.livePending = this.livePending.filter(p => p.executionId !== pc.executionId);
      convo.pendingConfirmation = undefined;
      // Deliberately leave convo.confirmExecutionId set. If the /pending read model lags and re-reports
      // this execution, the rebind must reuse THIS (now resolved) card rather than open a duplicate - a
      // node-less approval has no node to re-match on. It clears itself once /pending drops the id.
      convo.messages.push({ role: 'system', content: approved ? 'Approved.' : 'Rejected.' });
      convo.updatedAt = Date.now();
    } catch {
      convo.messages.push({ role: 'system', content: 'Could not submit the decision.' });
    } finally {
      convo.confirming = false;
      this.awaitingNodeIds = new Set(this.conversations.filter(c => c.pendingConfirmation).map(c => c.nodeId));
      this.save();
    }
  }

  // --- App-wide human-confirmation listener (app2 organigram_process). It lives on this ROOT service so
  // detection survives navigation: a 'waiting_for_human' execution surfaces on ANY page, not just the
  // graph. Torn down only when the app (root injector) is destroyed. ----------------------------------

  private startConfirmationListener(): void {
    this.confirmWantListen = true;
    this.attachConfirmationListener(++this.confirmGen);
  }

  // The cross-company list of actions awaiting this user (GET /pending, scoped to the user server-side,
  // one item per waiting approval with its own organigram_id). Polled so an approval in a company the
  // user is NOT currently viewing still surfaces - the per-company live listener alone would miss it.
  // The live listener only carries the newest executions, so this also catches ones sitting a while.
  private pollPending(): Promise<void> {
    if (!this.userId) { return Promise.resolve(); }   // needs X-User-Id; the poll retries on the next tick once authed
    if (this.blocked) { return Promise.resolve(); }
    return this.api.getAllPendingConfirmations().then(pending => {
      if (this.blocked) { return; }   // a refusal landed while the call was out
      const items = (pending || []).filter(p => p?.execution_id);
      // Make sure each involved company's node names are loading, so the cards read as names not ids.
      for (const org of new Set(items.map(p => p.organigram_id).filter(Boolean))) { this.ensureOrgNodes(org); }
      this.apiPending = items.map(p => {
        const org = p.organigram_id || this.organigramId;
        const info = this.nodeFor(org, p.node_id || '');
        return {
          executionId: p.execution_id, organigramId: org, nodeId: p.node_id || '',
          // /pending carries no node name, and often no node_id either - fall back to a readable label
          // rather than the raw id. When a node_id IS present, backfillNames swaps in the real name.
          nodeName: info?.name || p.node_id || 'Approval', nodeType: info?.type,
          message: p.message || p.user_message || '', requestId: p.request_id
        };
      });
      this.publishPending();
    }).catch(() => { /* endpoint unavailable / not authed - the live listener still covers the current company */ });
  }

  // The tasks assigned to THIS person, read from dailyTodos - see organigram-tasks.ts for why that
  // collection is the only place a message to a human node arrives.
  //
  // Filtered by the `uid` FIELD rather than assembled from the '{uid}_{date}' document id: a single
  // equality filter needs no composite index, and it does not break if the backend ever changes how
  // it names the documents. The day-level sort happens in tasksFromDocs, so no orderBy either.
  //
  // Not polled. These arrive by email as well and are not a live conversation, so the cost of a
  // 25s timer is not worth it - the dock refreshes them when it is opened.
  async loadTasks(): Promise<void> {
    if (!TASKS_ENABLED) { return; }   // no read grant yet - see TASKS_ENABLED in organigram-tasks.ts
    const uid = this.userId;
    if (!uid || !this.customerDB.app2) { return; }
    if (!await this.mayRead()) { return; }
    this.tasksLoading = this.tasks.length === 0;   // only show the loader on the first pass, not on refresh
    try {
      const snap = await this.customerDB.app2.firestore.collection('dailyTodos')
        .where('uid', '==', uid)
        .get();
      const docs = (snap?.docs || []).map((d: any) => ({ id: d.id, data: d.data() || {} }));
      if (!this.blocked) { this.tasks = tasksFromDocs(docs, localDateKey(new Date())); }
    } catch (e) {
      // A missing collection or a rules refusal is not worth an error card: the section simply stays
      // empty. Logged because it is also how a wrong account scope would show up.
      console.warn('[dock] could not load tasks', e);
    }
    this.tasksLoading = false;
  }

  // Tick / untick a task. The todos are an array field, so the whole array is rewritten with one
  // entry flipped - hence the re-read: writing a stale array would resurrect a todo someone else
  // (or another tab) already ticked. The row is updated locally first so the checkbox does not lag.
  async setTaskDone(task: OrgTask, done: boolean): Promise<void> {
    if (!TASKS_ENABLED || !task || !this.customerDB.app2) { return; }
    const before = task.done;
    task.done = done;
    const ref = this.customerDB.app2.firestore.collection('dailyTodos').doc(task.docId);
    try {
      const snap = await ref.get();
      const next = todosWithDone(snap?.data()?.todos || [], task.index, done);
      if (!next) { await this.loadTasks(); return; }   // the document moved on - take its version
      await ref.update({ todos: next });
    } catch (e) {
      task.done = before;   // put the checkbox back rather than lie about what was saved
      console.warn('[dock] could not update task', e);
    }
  }

  // Poll the cross-company pending list on a timer (plus an immediate first tick). Independent of the
  // company switcher, so it keeps running - and keeps surfacing approvals - whatever company is selected.
  private startPendingPoll(): void {
    if (this.pendingPollTimer) { return; }
    this.pollPending();
    this.pendingPollTimer = setInterval(() => this.pollPending(), 25000);
  }

  // orgId -> company name, for the row/card chips and the Browse company picker. Called once the
  // customer DB has authenticated, so X-Account-Id / X-User-Id are ready - no eager call, no polling.
  private async loadCompanyNames(): Promise<void> {
    if (this.blocked) { return; }
    try {
      const orgs = await this.api.listOrganigrams();
      if (this.blocked) { return; }
      for (const o of orgs || []) { if (o?.id) { this.companyNames.set(o.id, o.name || o.id); } }
    } catch { /* transient - re-attempted on the next auth emission */ }
  }
  companyName(orgId: string): string { return this.companyNames.get(orgId) || ''; }
  // Every company in scope, for the "start a new chat" company picker - so the directory is not stuck
  // on the currently-selected company. Alphabetical (German collation for Umlaute).
  get companyList(): { id: string; name: string }[] {
    return Array.from(this.companyNames, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }

  // Node name/type for ANY company. The graph is fetched once per org and cached; callers read the
  // cache synchronously (empty until the fetch lands, then backfillNames fixes the labels).
  private nodeFor(orgId: string, nodeId: string): { name: string; type?: string } | undefined {
    return this.orgNodeInfo.get(orgId)?.get(nodeId);
  }
  private async ensureOrgNodes(orgId: string): Promise<void> {
    if (!orgId || this.orgNodeInfo.has(orgId) || this.orgNodeLoading.has(orgId)) { return; }
    this.orgNodeLoading.add(orgId);
    try {
      if (!await this.mayRead()) { return; }
      const graph = await this.api.getGraph(orgId);
      if (this.blocked) { return; }
      const m = new Map<string, { name: string; type?: string }>();
      for (const n of graph?.nodes || []) { m.set(n.id, { name: n.name, type: n.type }); }
      this.orgNodeInfo.set(orgId, m);
      this.backfillNames();   // fix conversations/cards created before this company's names were known
    } catch {
      // A company whose graph fails (e.g. a backend 500 on corrupted data) caches an empty map: its
      // labels fall back to ids/message, but the poll never re-hits the failing endpoint every tick.
      this.orgNodeInfo.set(orgId, new Map());
    }
    finally { this.orgNodeLoading.delete(orgId); }
  }

  // Union of both sources, deduped by execution id. The live entries come last so their fresher
  // message/history wins over the REST snapshot for the same execution.
  private publishPending(): void {
    const byId = new Map<string, ConfirmationRequest>();
    for (const pc of [...this.apiPending, ...this.livePending]) { byId.set(pc.executionId, pc); }
    this.setPendingConfirmations([...byId.values()], this.organigramId);
  }

  // `gen` is bumped on every (re)start, so a pending app2 retry from a previous organigram
  // does not attach a second listener after the user has already switched company.
  private async attachConfirmationListener(gen: number): Promise<void> {
    if (!this.confirmWantListen || this.confirmUnsub || gen !== this.confirmGen) { return; }
    const db = this.customerDB.app2?.firestore;
    if (!db) { setTimeout(() => this.attachConfirmationListener(gen), 500); return; }   // app2 inits async - wait
    // No listener before the permission answer allows one - it reads this user's executions.
    if (!await this.mayRead()) { return; }
    // Resolve this company's node names BEFORE the first snapshot, so confirmation heads show the name.
    await this.ensureOrgNodes(this.organigramId);
    if (gen !== this.confirmGen || this.confirmUnsub) { return; }   // org may have switched during the await
    // The live query needs the user id (it filters on it), and app2 being ready does NOT mean the id has
    // loaded (they resolve on different timelines), so it waits for it here - else the backend rejects
    // with "X-User-Id ... must not be blank". The chat list and pending approvals are loaded separately
    // (loadAllSessions / startPendingPoll on the auth signal), independent of this per-company listener.
    this.whenAuthed(gen, () => {
      this.subscribeProcess(gen, db);
    });
  }

  // Every execution is denormalized with the INITIATING user's `user_id`, nested agent-to-agent hops
  // included, so filtering on it server-side leaves a request's thinking trace whole while reading
  // only this user's work. Was an org-wide window filtered in onProcessDocs; that needed no index but
  // read every user's executions. Requires the composite index (user_id ASC, created_at DESC) on
  // organigram_process - without it the query fails and the dock falls back to its REST pending list.
  // The window can be small now that it is one user's: older approvals - and every other company's -
  // come from the cross-company pending poll (getAllPendingConfirmations).
  private subscribeProcess(gen: number, db: any): void {
    if (!this.confirmWantListen || this.confirmUnsub || gen !== this.confirmGen) { return; }
    const path = this.api.processCollectionPath(this.organigramId);
    // No organigram yet: nothing to listen to. Left unsubscribed rather than retried - the id
    // arrives through the currentOrganigramId$ subscription, which re-runs this when it does.
    if (!path) { return; }
    this.confirmUnsub = db.collection(path)
      .where('user_id', '==', this.userId)
      .orderBy('created_at', 'desc')
      .limit(50)
      .onSnapshot(
        (snap: any) => this.zone.run(() => {
          if (this.blocked) { return; }   // a snapshot already queued when a refusal tore the listener down
          this.onProcessDocs((snap?.docs || []).map((d: any) => ({ id: d.id, ...d.data() })));
        }),
        // Keep the dock as-is on a transient error, but do NOT swallow it: a missing composite index
        // reports as failed-precondition here, and its message carries the console link that creates it.
        (err: any) => console.warn('[OrgChatDock] process listener error:', err)
      );
  }

  // The REST session/pending calls require X-User-Id, which loads async (Firebase auth) and is NOT
  // ready just because app2 is. Run `then` once the user id is non-blank, retrying briefly; give up
  // after ~30s rather than spin forever on a genuinely unauthed session.
  private whenAuthed(gen: number, then: () => void, tries = 0): void {
    if (gen !== this.confirmGen) { return; }
    if (this.userId) { then(); return; }
    if (tries >= 60) { return; }
    setTimeout(() => this.whenAuthed(gen, then, tries + 1), 500);
  }

  // Same wait-for-user-id as whenAuthed, but NOT tied to confirmGen (the company switcher must not
  // cancel the global auth-signal loads). Runs `then` once the id is non-blank; after ~30s it runs it
  // anyway so a genuinely unauthed session settles (loadAllSessions then bails and clears the loader).
  private whenUserId(then: () => void, tries = 0): void {
    if (this.userId || tries >= 60) { then(); return; }
    setTimeout(() => this.whenUserId(then, tries + 1), 500);
  }

  ngOnDestroy(): void {
    this.confirmWantListen = false;
    if (this.confirmUnsub) { this.confirmUnsub(); this.confirmUnsub = null; }
    if (this.pendingPollTimer) { clearInterval(this.pendingPollTimer); this.pendingPollTimer = null; }
    for (const t of this.labelTimers.values()) { clearTimeout(t); }
    this.labelTimers.clear();
  }

  // A conversation created before its company's node names loaded shows the node id as the name; once
  // that company's names are available, replace them with the real name/type. Cross-company: each
  // conversation is resolved against ITS OWN company's cache, never the current one.
  private backfillNames(): void {
    let changed = false;
    for (const c of this.conversations) {
      const info = this.nodeFor(c.organigramId, c.nodeId);
      if (!info) { continue; }
      if (c.nodeName === c.nodeId) { c.nodeName = info.name; changed = true; }
      if (!c.nodeType && info.type) { c.nodeType = info.type; changed = true; }
    }
    if (changed) { this.save(); }
  }

  private isWaitingForHuman(status?: string): boolean {
    return ['waiting_for_human', 'awaiting_human', 'awaiting_confirmation', 'waiting'].includes((status || '').toLowerCase());
  }

  // Only the HUMAN_ESCALATION task can be confirmed. When an agent asks for approval TWO executions
  // sit in 'waiting_for_human': the agent's own (which the backend resumes itself, via this task's
  // parent_execution_id) and this task on the human node. Posting the agent's id is a 400
  // "... is not a human confirmation task", so mode is what makes an execution confirmable.
  private isHumanTask(d: OrganigramProcess): boolean {
    return (d?.mode || '').toLowerCase() === 'human_escalation';
  }

  // Executions are denormalized with the owning user_id. subscribeProcess already filters on it in
  // the query, so this is defence in depth for anything reaching onProcessDocs another way. Strict
  // equality now: a doc carrying no user_id cannot come back from the filtered query, so treating a
  // missing field as "mine" would only ever let another user's work through.
  private isOwnedByUser(d: OrganigramProcess): boolean {
    const uid = this.userId;
    return !!uid && d?.user_id === uid;
  }

  // Turn a process snapshot into the live pending set, then publish it alongside the
  // REST list. An execution the REST list still reports as pending, but which this snapshot shows
  // as resolved, is dropped from that list - otherwise a stale card would outlive its approval.
  private onProcessDocs(docs: OrganigramProcess[]): void {
    const org = this.organigramId;   // the live listener is scoped to one company - the current one
    if (!this.orgNodeInfo.has(org)) { this.ensureOrgNodes(org); }   // names not ready - retry; backfill then fixes them
    const seen = new Set<string>();
    const list: ConfirmationRequest[] = [];
    const resolved = new Set<string>();
    for (const d of docs) {
      if (!this.isOwnedByUser(d)) { continue; }
      if (!this.isWaitingForHuman(d.status)) { resolved.add(d.id); continue; }
      if (!this.isHumanTask(d) || seen.has(d.id)) { continue; }
      seen.add(d.id);
      const requestId = d.request_id || d.id;
      const info = this.nodeFor(org, d.node_id || '');
      list.push({
        executionId: d.id, organigramId: org, nodeId: d.node_id || '', nodeName: info?.name || d.node_id || '(node)', nodeType: info?.type,
        message: d.message || d.response || '', requestId, history: this.buildConfirmationHistory(docs, requestId)
      });
    }
    this.livePending = list;
    if (resolved.size) { this.apiPending = this.apiPending.filter(pc => !resolved.has(pc.executionId)); }
    this.publishPending();
    this.applyTraces(docs);
  }

  // Stream the live process documents into whichever conversations have a request in flight, as the
  // thinking trace of the answer being waited on. The listener already carries every document, so
  // this costs no extra request - unlike GET /requests/{id}/traces, a snapshot endpoint the API
  // service explicitly warns against polling.
  private applyTraces(docs: OrganigramProcess[]): void {
    for (const c of this.conversations) {
      if (c.traceIndex === undefined || !c.requestId) { continue; }
      const msg = c.messages[c.traceIndex];
      if (!msg) { continue; }
      const hops = hopsForRequest(docs, c.requestId, id => this.nodeFor(c.organigramId, id)?.name || id);
      if (!hops.length) { continue; }
      if (!msg.trace) { msg.trace = []; }
      if (mergeHops(msg.trace, hops)) { c.updatedAt = Date.now(); }
      c.traceNodeIds = nodeIdsOf(msg.trace);
      this.queueLabels(c, msg);
    }
    this.recomputeActiveNodes();
  }

  // Turn any hop not yet announced into a live label and start the pump. Only hops beyond
  // traceShown are queued, so re-reading the same documents does not replay the whole run.
  private queueLabels(convo: OrgConversation, msg: OrgChatMessage): void {
    const hops = msg.trace || [];
    const from = convo.traceShown || 0;
    if (hops.length <= from) { return; }
    if (!convo.traceLabelQueue) { convo.traceLabelQueue = []; }
    for (let i = from; i < hops.length; i++) { convo.traceLabelQueue.push(hopLabel(hops[i])); }
    convo.traceShown = hops.length;
    this.pumpLabel(convo, msg);
  }

  // Drain the queue one label per LABEL_DWELL_MS, so a burst of fast hops still reads. The first
  // label waits out whatever is left of the current one's dwell rather than cutting it short.
  private pumpLabel(convo: OrgConversation, msg: OrgChatMessage): void {
    if (this.labelTimers.has(convo.id)) { return; }   // already draining
    const step = () => {
      this.labelTimers.delete(convo.id);
      const next = convo.traceLabelQueue?.shift();
      if (!next || !convo.sending) { return; }
      msg.traceLabel = next;
      convo.traceLabelAt = Date.now();
      this.labelTimers.set(convo.id, setTimeout(() => this.zone.run(step), LABEL_DWELL_MS));
    };
    const waited = Date.now() - (convo.traceLabelAt || 0);
    if (waited >= LABEL_DWELL_MS) { step(); return; }
    this.labelTimers.set(convo.id, setTimeout(() => this.zone.run(step), LABEL_DWELL_MS - waited));
  }

  // Stop a conversation's label pump and forget its queue.
  private stopLabels(convo: OrgConversation): void {
    const t = this.labelTimers.get(convo.id);
    if (t) { clearTimeout(t); this.labelTimers.delete(convo.id); }
    convo.traceLabelQueue = [];
  }

  // Union of every in-flight conversation's trace nodes. Kept as a plain Set field rather than a
  // getter: the graph template calls .has() once per card on every change-detection pass.
  private recomputeActiveNodes(): void {
    const out = new Set<string>();
    for (const c of this.conversations) {
      if (!c.sending) { continue; }
      for (const id of c.traceNodeIds || []) { out.add(id); }
    }
    this.activeNodeIds = out;
  }

  // Close out the thinking block: stamp how long the request took, so the header settles from
  // "Thinking..." to "Thought for Xs". Its open/closed state is left alone - the block is already
  // closed unless the reader opened it, and shutting it under them mid-read would be worse.
  private finishTrace(convo: OrgConversation, idx: number): void {
    const msg = convo.messages[idx];
    if (msg?.trace?.length) {
      msg.traceDone = true;
      msg.traceMs = Date.now() - (convo.traceStart || Date.now());
    }
    convo.traceIndex = undefined;
    convo.traceNodeIds = [];
    convo.traceShown = 0;
    this.stopLabels(convo);
    this.recomputeActiveNodes();
  }

  // The request's lead-up trace as chat messages, so a confirmation opens WITH context (the executions
  // that ran before it) instead of an empty box.
  private buildConfirmationHistory(docs: any[], requestId: string): OrgChatMessage[] {
    const tsOf = (d: any) => (d.created_at?.seconds || 0) + (d.created_at?.nanoseconds || 0) / 1e9;
    const rel = docs.filter(d => (d.request_id || d.id) === requestId).sort((a, b) => tsOf(a) - tsOf(b));
    const msgs: OrgChatMessage[] = [];
    for (const d of rel) {
      // Built from the current company's live docs, so resolve names against the current org's cache.
      const recip = this.nodeFor(this.organigramId, d.node_id)?.name || d.node_id;
      const sender = this.nodeFor(this.organigramId, d.sender_node_id)?.name;
      if ((d.sender_type === 'user' || !d.sender_node_id) && d.message) {
        msgs.push({ role: 'user', content: d.message });
      } else if (d.message) {
        msgs.push({ role: 'assistant', content: sender ? `${sender} → ${recip}: ${d.message}` : `${recip}: ${d.message}` });
      }
      if (d.response) { msgs.push({ role: 'assistant', content: `${recip}: ${d.response}` }); }
    }
    return msgs;
  }

  // --- Backend-owned chat list (GET /sessions) --------------------------------------------------
  // A session IS the conversation as the backend knows it. This is the source of truth for the Recent
  // list: it spans every company (listAllSessions), so a chat started on another device or company
  // shows up here. Messages load lazily on open (getSessionHistory). Called on auth and on dock open.

  async loadAllSessions(): Promise<void> {
    if (!this.userId) { this.hydratingSessions = false; return; }   // not authed - whenUserId drives the real load
    // Waited on, not just checked: opening the rail before the first answer must not list anything.
    if (!await this.mayRead()) { this.hydratingSessions = false; return; }
    let sessions: OrganigramSessionSummary[];
    try {
      sessions = await this.api.listAllSessions();
    } catch { this.hydratingSessions = false; return; }   // backend unavailable - surfaces as an empty list, never masked
    if (this.blocked) { this.hydratingSessions = false; return; }   // a refusal landed while the call was out
    const known = new Set(this.conversations.map(c => c.sessionId).filter(Boolean));
    const orgs = new Set<string>();
    for (const s of (sessions || []).slice(0, MAX_HYDRATED_SESSIONS)) {
      if (!s?.session_id || known.has(s.session_id)) { continue; }
      known.add(s.session_id);
      const org = s.organigram_id || this.organigramId;
      orgs.add(org);
      const info = this.nodeFor(org, s.node_id || '');
      // The summary carries only session_id / organigram_id / node_id / updatedISO (an ISO string).
      const at = toMillis(s.updatedISO);
      this.conversations.push({
        id: `s-${s.session_id}`, organigramId: org, nodeId: s.node_id || '',
        // Labelled by its node until the history is pulled on open; backfillNames swaps in the real
        // name once that company's graph loads (triggered below).
        nodeName: info?.name || s.node_id || '(node)', nodeType: info?.type, sessionId: s.session_id,
        title: info?.name ? `Chat with ${info.name}` : 'Chat',
        messages: [], draft: '', sending: false, open: false, minimized: false,
        createdAt: at, updatedAt: at
      });
    }
    for (const org of orgs) { this.ensureOrgNodes(org); }   // resolve node names; backfillNames fixes the rows
    this.hydratingSessions = false;
  }

  // Pull one session's messages the first time its conversation is opened, or when the Recent
  // group expands and needs a last-message preview. Conversations this browser already holds carry
  // their own history, so only empty ones are fetched.
  private async loadSessionHistory(convo: OrgConversation): Promise<void> {
    if (!convo.sessionId || convo.messages.length || convo.loadingHistory) { return; }
    convo.loadingHistory = true;
    try {
      if (!await this.mayRead()) { return; }
      const history = await this.api.getSessionHistory(convo.organigramId, convo.nodeId, convo.sessionId);
      if (this.blocked) { return; }
      if (!convo.messages.length) { convo.messages = toChatMessages(history); }
      if (isGenericTitle(convo.title)) {
        const snippet = firstUserSnippet(convo.messages);
        if (snippet) { convo.title = snippet; }
      }
      this.save();
    } catch { /* history unavailable - the conversation simply opens empty */ } finally {
      convo.loadingHistory = false;
    }
  }

  // Recent-group expand: fetch histories so each folded row can show what that session was about.
  ensureHistories(convos: OrgConversation[]): Promise<void> {
    return Promise.all((convos || []).map(c => this.loadSessionHistory(c))).then(() => undefined);
  }

  openConversation(convo: OrgConversation): void {
    convo.open = true;
    convo.minimized = false;
    this.loadSessionHistory(convo);   // a session opened here for the first time has no local messages
  }
  closeWindow(convo: OrgConversation): void { convo.open = false; convo.minimized = false; this.save(); }   // keep in history
  deleteConversation(convo: OrgConversation): void { this.conversations = this.conversations.filter(c => c !== convo); this.save(); }
  // Refresh Recent and the assigned tasks from the backend on open - tasks are not polled, so this
  // is what picks up one raised while the dock was shut.
  toggleRail(): void { this.railOpen = !this.railOpen; if (this.railOpen) { this.loadAllSessions(); this.loadTasks(); } }
  closeRail(): void { this.railOpen = false; }

  async send(convo: OrgConversation): Promise<void> {
    const text = convo.draft?.trim();
    if (!text || convo.sending) { return; }
    convo.messages.push({ role: 'user', content: text });
    if (!convo.title || convo.title === 'New chat') { convo.title = text.slice(0, 40); }
    convo.draft = '';
    convo.updatedAt = Date.now();

    // Humans are one-way: the message is delivered, but their reply doesn't come back here,
    // so we don't poll - we just confirm delivery.
    if (convo.nodeType === 'human') {
      convo.sending = true;
      this.save();
      try {
        const res = await this.api.startMessage(convo.organigramId, convo.nodeId, text, convo.sessionId);
        if (res?.session_id) { convo.sessionId = res.session_id; }
        if (this.conversations.includes(convo)) {
          convo.messages.push({ role: 'system', content: `Delivered to ${convo.nodeName}. One-way - their reply won't appear here.` });
        }
      } catch {
        if (this.conversations.includes(convo)) {
          convo.messages.push({ role: 'system', content: 'Could not deliver the message.' });
        }
      } finally {
        convo.sending = false;
        convo.unread = true;
        convo.updatedAt = Date.now();
        this.save();
      }
      return;
    }

    // Agents / teams: poll for the response. The hops it takes through the org stream into this
    // message's thinking block from the live process listener (applyTraces). The block starts
    // CLOSED and is never opened for you - it is there to be read on demand, not to be watched.
    convo.sending = true;
    convo.messages.push({ role: 'assistant', content: '' });
    const idx = convo.messages.length - 1;
    convo.traceIndex = idx;
    convo.traceStart = Date.now();
    this.save();
    try {
      // Passing the conversation's session id makes this a follow-up: the backend replays that
      // session's routing context for the entry node instead of starting cold. A blank one mints a
      // new session, whose id comes back on the response and is kept for the rest of the chat.
      const { request_id, session_id } = await this.api.startMessage(convo.organigramId, convo.nodeId, text, convo.sessionId);
      convo.requestId = request_id;   // matches this run's live process docs into the thinking-trace block
      if (session_id) { convo.sessionId = session_id; }
      const req = await this.awaitRequest(convo, request_id);
      // No req means the conversation was deleted while we waited - there is nothing to write to.
      if (!req || !this.conversations.includes(convo)) { return; }
      if (this.isFailed(req.status)) {
        convo.messages[idx].content = this.extractError(req) || 'The request failed.';
      } else {
        convo.messages[idx].content = this.extractResponse(req) || 'No response returned.';
      }
      convo.unread = true;   // reply arrived - shows as a bottom chat head if this convo isn't focused
    } catch (err: any) {
      if (this.conversations.includes(convo)) {
        convo.messages[idx].content = 'Sorry - something went wrong sending that message.';
      }
    } finally {
      convo.sending = false;
      this.finishTrace(convo, idx);
      convo.updatedAt = Date.now();
      this.save();
    }
  }

  // Poll the request until it goes terminal. Deliberately unbounded - the only exit other than a
  // terminal status is the conversation being deleted, which returns null.
  private async awaitRequest(convo: OrgConversation, requestId: string): Promise<any | null> {
    const started = Date.now();
    for (;;) {
      await this.delay(replyPollDelay(Date.now() - started));
      if (!this.conversations.includes(convo)) { return null; }
      try {
        const req = await this.api.getRequest(convo.organigramId, requestId);
        if (this.isTerminal(req)) { return req; }
      } catch { /* transient - keep polling */ }
    }
  }

  // Run one node for real, once, with a one-off sample - the same start_message primitive send() uses
  // above, but standalone (no conversation, no kept session). Returns a normalised result so the caller
  // needs none of the private terminal/response helpers. Used by the edit-node panel's "Run this step".
  async runStepOnce(organigramId: string, nodeId: string, sample: string): Promise<{ ok: boolean; text: string }> {
    const { request_id } = await this.api.startMessage(organigramId, nodeId, sample, '');
    const req = await this.pollUntilTerminal(organigramId, request_id);
    if (this.isFailed(req?.status)) { return { ok: false, text: this.extractError(req) || 'The run failed.' }; }
    return { ok: true, text: this.extractResponse(req) || 'No response returned.' };
  }

  // Poll a request id until it goes terminal. Unbounded, with the same backoff as awaitRequest; takes a
  // plain request id, so it does not need a conversation to check for deletion the way awaitRequest does.
  private async pollUntilTerminal(organigramId: string, requestId: string): Promise<any> {
    const started = Date.now();
    for (;;) {
      await this.delay(replyPollDelay(Date.now() - started));
      try {
        const req = await this.api.getRequest(organigramId, requestId);
        if (this.isTerminal(req)) { return req; }
      } catch { /* transient - keep polling */ }
    }
  }

  private isTerminal(req: any): boolean {
    return !!req?.completed_at || this.isFailed(req?.status) || this.isDone(req?.status);
  }
  private isDone(status?: string): boolean {
    return ['completed', 'complete', 'done', 'finished', 'success', 'succeeded'].includes((status || '').toLowerCase());
  }
  private isFailed(status?: string): boolean {
    return ['error', 'failed', 'cancelled', 'canceled'].includes((status || '').toLowerCase());
  }
  private extractResponse(req: any): string { return req?.response || ''; }
  private extractError(req: any): string { return req?.error || req?.error_message || ''; }
  private delay(ms: number): Promise<void> { return new Promise(resolve => setTimeout(resolve, ms)); }

  // The backend is the source of truth, so conversations are NOT persisted to localStorage: a stale
  // local cache would mask a broken backend (the UI would look healthy while the server was failing)
  // and drift from it over time. This stays as the single persistence seam - the many callers keep
  // working - so a deliberate cache (metadata-only, say) could be reintroduced here in ONE place if
  // ever wanted, without it silently becoming the source of truth again.
  private save(): void { /* intentionally not persisted - see note above */ }
}
